import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { TabIntro } from "@/components/page-tabs";
import { PersonPicker } from "@/components/person-picker";
import { Button } from "@/components/ui/button";
import {
  type ConnectorOverview,
  type Team,
  type TeamsOverview,
  useAdminTeams,
  useConnectorOverview,
} from "@/lib/api";
import { useLanguage } from "@/lib/i18n/language";
import { withGroupText, withToolText } from "@/lib/i18n/tools";
import { personLabel } from "@/lib/people";

import { BlockDialog, type BlockTarget } from "./block-dialog";
import { ExceptionsEditor } from "./exceptions-editor";
import { PeopleTable } from "./people-table";
import { TeamDeleteDialog } from "./team-delete-dialog";
import { TeamEditor } from "./team-editor";
import { TeamTable } from "./team-table";
import { appGrants, grantRows, peopleByEmail, teamUnitTools } from "./utils";

/** The team editor: closed, a new team, or a team to edit. */
type Editing = { team: Team | null } | null;

/**
 * Mensen en teams → Teams (admins, `/people/teams`): which tools Claude may
 * use for each colleague, and which apps they may open. Teams give tool
 * groups (reading and writing apart) and apps; someone in no team gets
 * "Iedereen zonder team", which never gives an app; the fixed Beheerders
 * team (every dashboard admin and its extra members) gets everything; an
 * exception for one person wins over their teams. The MCP server applies the
 * same rules; this tab shows them. The people table also blocks and unblocks
 * someone's use of DC Data.
 */
export function TeamsPage() {
  const { t } = useTranslation();
  const teamsQuery = useAdminTeams();
  const overviewQuery = useConnectorOverview();
  const [editing, setEditing] = useState<Editing>(null);

  const loading = teamsQuery.isLoading || overviewQuery.isLoading;
  const teams = teamsQuery.data;
  const catalogue = overviewQuery.data;

  return (
    <>
      <TabIntro
        description={
          <>
            <span className="block">{t("teams.explainTeams")}</span>
            <span className="block">{t("teams.explainExceptions")}</span>
          </>
        }
        actions={
          <Button onClick={() => setEditing({ team: null })} disabled={!teams || !catalogue}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t("teams.newTeam")}
          </Button>
        }
      />
      {loading ? (
        <LoadingCard />
      ) : !teams || !catalogue ? (
        <LoadErrorCard
          message={t("teams.loadError")}
          onRetry={() => {
            void teamsQuery.refetch();
            void overviewQuery.refetch();
          }}
        />
      ) : (
        <TeamsView
          overview={teams}
          catalogue={catalogue}
          editing={editing}
          onEditingChange={setEditing}
        />
      )}
    </>
  );
}

interface TeamsViewProps {
  overview: TeamsOverview;
  catalogue: ConnectorOverview;
  editing: Editing;
  onEditingChange: (editing: Editing) => void;
}

function TeamsView({ overview, catalogue, editing, onEditingChange }: TeamsViewProps) {
  const { t } = useTranslation();
  const language = useLanguage();
  const [deleting, setDeleting] = useState<Team | null>(null);
  const [exceptionsFor, setExceptionsFor] = useState<string | null>(null);
  const [blocking, setBlocking] = useState<BlockTarget | null>(null);
  // What the dialogs last showed: they keep it while they fade out after closing.
  const [lastEditing, setLastEditing] = useState(editing);
  if (editing !== null && editing !== lastEditing) {
    setLastEditing(editing);
  }
  const [lastDeleting, setLastDeleting] = useState(deleting);
  if (deleting !== null && deleting !== lastDeleting) {
    setLastDeleting(deleting);
  }
  const [lastExceptionsFor, setLastExceptionsFor] = useState(exceptionsFor);
  if (exceptionsFor !== null && exceptionsFor !== lastExceptionsFor) {
    setLastExceptionsFor(exceptionsFor);
  }
  const [lastBlocking, setLastBlocking] = useState(blocking);
  if (blocking !== null && blocking !== lastBlocking) {
    setLastBlocking(blocking);
  }

  // Only the tools a team or an exception can name: never a role's tool or a submission.
  const rows = grantRows(
    withGroupText(catalogue.groups, language),
    withToolText(teamUnitTools(catalogue.tools), language),
  );
  // The apps a team can be given, by name in the dashboard's language.
  const apps = appGrants(overview.apps);
  const people = peopleByEmail(overview.people);
  const withoutTeam = overview.people.filter((person) => person.teams.length === 0).length;
  // The domains an added address must have; the server checks them again.
  const domains = overview.email_domains;
  const exceptionsEmail = exceptionsFor ?? lastExceptionsFor;
  const exceptionsPerson = exceptionsEmail ? people.get(exceptionsEmail) : undefined;

  return (
    <div className="space-y-10">
      <TeamTable
        defaultTeam={overview.default_team}
        adminsTeam={overview.admins_team}
        teams={overview.teams}
        rows={rows}
        apps={apps}
        people={people}
        withoutTeam={withoutTeam}
        onEdit={(team) => onEditingChange({ team })}
        onDelete={setDeleting}
      />

      <section aria-labelledby="teams-people" className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <h2 id="teams-people" className="font-heading text-primary text-lg font-bold">
            {t("teams.people.title")}
          </h2>
          <PersonPicker
            people={overview.people}
            label={t("teams.people.addException")}
            onPick={(email) => setExceptionsFor(email)}
            addByEmail={{ domains }}
            className="shrink-0 sm:w-60"
          />
        </div>
        <PeopleTable
          overview={overview}
          rows={rows}
          apps={apps}
          onExceptions={setExceptionsFor}
          onBlock={setBlocking}
        />
      </section>

      <TeamEditor
        team={(editing ?? lastEditing)?.team ?? null}
        open={editing !== null}
        onOpenChange={(open) => !open && onEditingChange(null)}
        rows={rows}
        apps={apps}
        people={overview.people}
        domains={domains}
      />
      <TeamDeleteDialog
        team={deleting ?? lastDeleting}
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
      />
      <ExceptionsEditor
        email={exceptionsEmail}
        open={exceptionsFor !== null}
        label={exceptionsPerson ? personLabel(exceptionsPerson) : (exceptionsEmail ?? "")}
        overview={overview}
        rows={rows}
        apps={apps}
        onOpenChange={(open) => !open && setExceptionsFor(null)}
      />
      <BlockDialog
        person={blocking ?? lastBlocking}
        open={blocking !== null}
        onOpenChange={(open) => !open && setBlocking(null)}
      />
    </div>
  );
}
